export type ConsentFlowStep = {
  /** i18n key — resolve at call site via intl.formatMessage({ id: step.labelKey }) */
  labelKey: string
  titleKey: string
  descriptionKey: string
}

export type ConsentFlowSteps = {
  consent: ConsentFlowStep
  secondary: ConsentFlowStep
  profile: ConsentFlowStep
}

/**
 * Mode A ("practitioner mediated"): PRACTITIONER_MEDIATED, the legacy typo PRACTIONER_MEDIATED from older
 * client-config defaults, and Mode AB (PRACTITIONER_MEDIATED_AB). AB behaves exactly like Mode A for the
 * proband and every order-level behavior; it is never Mode B.
 */
export function isPractitionerMediatedConsentFlow(consentFlow: string | null | undefined): boolean {
  const f = (consentFlow ?? '').trim().toUpperCase()
  return f === 'PRACTITIONER_MEDIATED' || f === 'PRACTIONER_MEDIATED' || f === 'PRACTITIONER_MEDIATED_AB'
}

export function isPrimaryConsentFlow(consentFlow: string | null | undefined): boolean {
  return (consentFlow ?? '').trim().toUpperCase() === 'PRIMARY'
}

export function buildConsentFlowSteps(consentFlow: string | null | undefined): ConsentFlowSteps {
  const practitionerMediated = isPractitionerMediatedConsentFlow(consentFlow)
  return {
    consent: {
      labelKey: 'consent.flow.consent.label',
      titleKey: 'consent.flow.consent.title',
      descriptionKey: 'consent.flow.consent.description',
    },
    secondary: {
      labelKey: practitionerMediated ? 'consent.flow.secondary.labelStep1' : 'consent.flow.secondary.labelStep2',
      titleKey: 'consent.flow.secondary.title',
      descriptionKey: 'consent.flow.secondary.description',
    },
    profile: {
      labelKey: practitionerMediated ? 'consent.flow.profile.labelStep2' : 'consent.flow.profile.labelStep3',
      titleKey: 'consent.flow.profile.title',
      descriptionKey: 'consent.flow.profile.description',
    },
  }
}

/** Subset of GET /consents/context needed to resolve the flow for the signed-in user. */
export type UserConsentFlowContext = {
  consentFlow?: string | null
  primaryConsentCheckboxVariant?: 'guardian' | 'comparator' | null
  /** The signed-in comparator's own per-order value (FAM-205). */
  platformCollectsConsentToTest?: boolean | null
}

/** True when the signed-in user is a comparator whose per-order value was not recorded (a data defect). */
export function isComparatorConsentValueMissing(context: UserConsentFlowContext | null | undefined): boolean {
  return (
    context?.primaryConsentCheckboxVariant === 'comparator' &&
    typeof context.platformCollectsConsentToTest !== 'boolean'
  )
}

/**
 * Consent flow for the signed-in user. Resolved per user, not per order: a comparator never branches on
 * the order mode, only on their per-order value (true -> Mode B primary-consent flow, false -> not
 * collected). A missing value is never inferred from the mode. Proband and guardian keep the order flow
 * (so Mode AB behaves as Mode A for them).
 */
export function resolveUserConsentFlow(context: UserConsentFlowContext | null | undefined): string | null {
  if (!context) return null
  if (context.primaryConsentCheckboxVariant === 'comparator') {
    return context.platformCollectsConsentToTest === true ? 'PRIMARY' : 'PRACTITIONER_MEDIATED'
  }
  return context.consentFlow ?? null
}
